import React from 'react';

import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

/** What a seat says about itself when asked, where the reader has more to say than one line. */
export interface SeatInfo {
	/** Who is in it. */
	heading?: string;
	fareType?: { name: string; colour?: string | null };
	/** Drawn as the card's border — the seat's own amber or green — rather than said in words. */
	status?: { label: string; colour: string };
	/** Anything else, in a sentence — "Wheelchair bay — not bookable as a seat". */
	note?: string;
}

/** Where the seat that was pressed sits on screen, in viewport coordinates. */
export interface SeatAnchor {
	top: number;
	bottom: number;
	centre: number;
}

interface Props {
	seatLabel: string;
	anchor: SeatAnchor;
	/** Structured detail where the reader supplied it; otherwise `fallback` is shown as a sentence. */
	info?: SeatInfo;
	fallback: string;
	onClose(): void;
}

const EDGE = 8;
const GAP = 8;

/**
 * The card a seat opens, pinned beside it.
 *
 * <p>Laid out like the fare type's `ClassInfoPopover` — who as the heading, then their seat and fare
 * type — so it reads at a glance rather than as one run-on line of dashes. Whether they are aboard
 * is the card's border, in the same amber or green the seat is drawn in, rather than words.</p>
 *
 * <p>Placed after it has been measured, because only then is its size known: centred under the
 * seat, pulled back inside the viewport where that would cross an edge (a seat at the side of a
 * phone-width plan), and turned above the seat where there is no room below. It stays hidden for
 * the one frame before that, so it never flashes in the wrong place.</p>
 */
export default function SeatInfoPanel({ seatLabel, anchor, info, fallback, onClose }: Props) {
	const ref = React.useRef<HTMLDivElement>(null);
	const [position, setPosition] = React.useState<{ top: number; left: number } | null>(null);

	React.useLayoutEffect(() => {
		const panel = ref.current;
		if (!panel) {
			return;
		}
		const { width, height } = panel.getBoundingClientRect();
		const maxLeft = window.innerWidth - width - EDGE;
		const left = Math.max(EDGE, Math.min(anchor.centre - width / 2, maxLeft));

		let top = anchor.bottom + GAP;
		if (top + height > window.innerHeight - EDGE) {
			top = Math.max(EDGE, anchor.top - GAP - height);
		}
		setPosition({ top, left });
	}, [anchor.top, anchor.bottom, anchor.centre, info, fallback]);

	const hasDetail = !!info && (!!info.heading || !!info.fareType || !!info.status || !!info.note);
	// A reader with only a word to say ("already taken") gets it as the line under the seat.
	const note = hasDetail ? info?.note : fallback;

	return (
		<div
			ref={ref}
			role="tooltip"
			className="seat-info"
			style={{
				...(position ? { top: position.top, left: position.left } : { top: 0, left: 0, visibility: 'hidden' }),
				borderColor: info?.status?.colour,
			}}
			// The panel is not a control; a tap on it is a tap outside every seat, which is how the
			// reader dismisses it.
			onClick={onClose}
		>
			{info?.heading && (
				<p className="seat-info__who">
					<Icon name="person" useMaterialIcons classname="seat-info__icon" />
					{info.heading}
				</p>
			)}
			<p className="seat-info__seat">
				<Icon name="event_seat" useMaterialIcons classname="seat-info__icon" />
				{seatLabel}
			</p>
			{info?.fareType && (
				<p className="seat-info__line">
					<span
						className="seat-info__swatch seat-info__swatch--round"
						style={{ background: info.fareType.colour || '#9ca3af' }}
					/>
					{info.fareType.name}
				</p>
			)}
			{note && <p className="seat-info__note">{note}</p>}
		</div>
	);
}
